// kvd
//   ESRecordMapper allows us to:
//     Maintain the flags on ESRecord, whenever a row is created/ saved/ destroyed.
//           Decoupled from any Adapter
//     Support soft deletes
//     Support modified timestamp

import {Mapper} from 'js-data';
import {ESRecord} from "./ESRecord";
import * as _ from 'lodash';
import {ESDataStoredefineMapperOptions} from "./ESDataStore";
import {IKeyGenerator} from "./libIF";


class ESRecordMapper extends Mapper {

    bDebugCalls: boolean = false;

    private _createdDtTsFieldName: string ;
    private _numberProperties: string[];
    private _numberPrecisions: { [propName: string]: number };
    private _dctPropertyTypes: {[propName: string]: string};
    private _stringPropertyLengths: { [propName: string]: number };
    private _stringAutoTrimFields: string[];
    private _logicalKeys: string[];
    private _nonParentForeignKeys: {[columnName: string]: string};
    private readonly _bDeleteIndicatorExists: boolean = false;
    private readonly _deletedIndicatorDeleteValue: string = '1';
    private readonly _deletedIndicatorFieldName: string = '';
    private readonly _modifiedDtTsFieldName: string = '';
    private readonly _tableName: string = '';
    private readonly _bManualIndicatorExists: boolean = false;
    private readonly _manualIndicatorFieldName: string = '';
    private readonly _manualIndicatorFieldValue: string = '';
    private _properties: string[];
    private _keyGenerator: IKeyGenerator;

    constructor(options: ESDataStoredefineMapperOptions) {
        super(options);
        let opts = options || {}
        const self = this;
        self._properties = [];
        self._numberProperties = [];
        self._numberPrecisions = {};
        self._dctPropertyTypes = {};
        self._stringPropertyLengths = {};
        self._stringAutoTrimFields = [];
        _.forEach(opts.schema.properties, (aProp, aKey) => {
            self._properties.push(aKey);
            switch (aProp.type) {
                case "number":
                    self._numberProperties.push(aKey);
                    self._dctPropertyTypes[aKey] = "number";
                    // for decimal properties, store its scale
                    if (aProp.hdb && aProp.hdb.dataScale && aProp.hdb.dataScale > 0) {
                        self._numberPrecisions[aKey] = aProp.hdb.dataScale
                    }
                    break;
                case "string":
                    self._stringPropertyLengths[aKey] = aProp.length;
                    self._dctPropertyTypes[aKey] = "string";
                    break;
            }

            // Auto Declare options based on schema fields
            switch(aKey) {

                case "DELETED_IND":
                    if(!('recDeletedIndicatorFieldName' in opts))
                        opts.recDeletedIndicatorFieldName = aKey
                    break;

                case "MANUAL_IND":
                    if(!('recManualIndicatorFieldName' in opts))
                        opts.recManualIndicatorFieldName = aKey
                    break;

                case "MODIFIED_DTTS":
                    if(!('recModifiedDtTsFieldName' in opts))
                        opts.recModifiedDtTsFieldName = aKey
                    break;

                case "CREATED_DTTS":
                    if(!('recCreatedDtTsFieldName' in opts))
                        opts.recCreatedDtTsFieldName = aKey
                    break;
            }

        });

        this._createdDtTsFieldName = '';
        this._modifiedDtTsFieldName = '';

        this._bDeleteIndicatorExists = false;
        this._deletedIndicatorFieldName = '';
        this._deletedIndicatorDeleteValue = '1';

        this._manualIndicatorFieldValue = '1';

        // Get the Created/ Modified DtTs field when available
        if (!_.isEmpty(opts.recModifiedDtTsFieldName)) {
            this._modifiedDtTsFieldName = opts.recModifiedDtTsFieldName;
        }

        if (!_.isEmpty(opts.recCreatedDtTsFieldName)) {
            this._createdDtTsFieldName = opts.recCreatedDtTsFieldName;
        }

        if (!_.isEmpty(opts.recDeletedIndicatorFieldName)) {
            this._deletedIndicatorFieldName = opts.recDeletedIndicatorFieldName;
            this._bDeleteIndicatorExists = true;
        }

        if (!_.isEmpty(opts.recDeletedIndicatorDeleteValue)) {
            this._deletedIndicatorDeleteValue = opts.recDeletedIndicatorDeleteValue;
        }

        if (!_.isEmpty(opts.recManualIndicatorFieldName)) {
            this._manualIndicatorFieldName = opts.recManualIndicatorFieldName;
            this._bManualIndicatorExists = true;
        }

        if (!_.isEmpty(opts.recManualIndicatorFieldValue)) {
            this._manualIndicatorFieldValue = opts.recManualIndicatorFieldValue;
        }

        if (!_.isEmpty(opts.recAutoTrimFields)) {
            this._stringAutoTrimFields = opts.recAutoTrimFields;
        }

        if (!_.isEmpty(opts.recLogicalKeys)) {
            this._logicalKeys = opts.recLogicalKeys;
        }

        if (!_.isEmpty(opts.recNonParentForeignKeys)) {
            this._nonParentForeignKeys = opts.recNonParentForeignKeys;
        }

        if (!_.isEmpty(opts.recTableName)) {
            this._tableName = opts.recTableName;
            // @ts-ignore
            opts.schema.options = {
                hdb: {
                    table: opts.recTableName
                }
            }
        }

        if (!_.isEmpty(opts.keyGenerator)) {
            this._keyGenerator = opts.keyGenerator;
        }

    }

    //<editor-fold desc="Properties">

    get DeletedIndicatorFieldName(): string {
        return this._deletedIndicatorFieldName;
    }

    get DeletedIndicatorDeleteValue(): string {
        return this._deletedIndicatorDeleteValue;
    }

    get bDeleteIndicatorExists(): boolean {
        return this._bDeleteIndicatorExists;
    }

    get bManualIndicatorExists(): boolean {
        return this._bManualIndicatorExists;
    }

    get ManualIndicatorFieldName(): string {
        return this._manualIndicatorFieldName;
    }

    get ManualIndicatorFieldValue(): string {
        return this._manualIndicatorFieldValue;
    }

    get CreatedDtTsFieldName(): string {
        return this._createdDtTsFieldName;
    }

    get ModifiedDtTsFieldName(): string {
        return this._modifiedDtTsFieldName;
    }
    
    get NumberProperties(): string[] {
        return this._numberProperties;
    }

    get StringPropertyLengths(): { [propName: string]: number } {
        return this._stringPropertyLengths
    }

    get Properties(): string[] {
        return this._properties;
    }

    get LogicalKeys(): string[] {
        return this._logicalKeys;
    }

    get ForeignKeys(): any[] {
        return this.relationList;
    }

    get NonParentForeignkeys(): {[columnName: string]: string} {
        return this._nonParentForeignKeys || {};
    }

    get IdAttribute(): string {
        return this.idAttribute;
    }

    get KeyGenerator(): IKeyGenerator {
        return this._keyGenerator;
    }

    get TableName(): string {
        return this._tableName;
    }

    getPropertyType(propName: string): string {
        return this._dctPropertyTypes[propName];
    }

    public getUILogicalKey(record: ESRecord): string {

        if(_.isEmpty(record))
            return "NA"

        if(_.isEmpty(this.LogicalKeys))
            return (<any> record)[this.IdAttribute]
        else
            return _.map(this.LogicalKeys, (lk) => (<any>record)[lk]).join(",")
    }

    public getUIName(): string {
        return this.name.replace("WB_","")
    }



    //</editor-fold>

    //<editor-fold desc="Before and after methods">

    createRecord(props?: any, opts?: any) {
        // From v3 docs, createRecord
        // Create an unsaved, uncached instance of this Mapper's Mapper#recordClass.
        // Returns props if props is already an instance of Mapper#recordClass.
        // Note: This method does not interact with any adapter, and does not save any data. It only creates new objects in memory.

        this.postProcessRow(props);

        var returnValue = super.createRecord(props, opts);

        // Mapper calls this to wrap an Object into a record instance.
        // props == Object
        //
        //if(props === returnValue) // incoming record is already wrapped.

        return (returnValue);
    }

    afterCreate(props: any, opts: any, result: any): any {

        // From v3 docs, create
        // Create and save a new the record using the provided props.
        //
        // Mapper#beforeCreate will be called before calling the adapter. Mapper#afterCreate will be called after calling the adapter.

        if (result instanceof ESRecord) {
            result._rowDeleted = false;
            result._rowExists = true;
        }
        ;
        // Can return a Promise<Record>

        return super.afterCreate(props, opts, result); // defaults to notify..
    };

    afterCreateMany(records: any[], opts: any, results: any): any {

        // Records is the array of Records.
        // From v3 docs, createMany
        // Given an array of records, batch create them via an adapter.
        //
        // Mapper#beforeCreateMany will be called before calling the adapter. Mapper#afterCreateMany will be called after calling the adapter.

        for (var idx in records) {
            if (records[idx] instanceof ESRecord) {
                records[idx]._row_deleted = false;
                records[idx]._row_exists = true;
            }
            ;
        }
        ;

        return super.afterCreateMany(records, opts, results);
    };


    //  For all the "Crud" operations (except create)...
    //  the after & before calls have identical arguments to the standard call.. plus the results.. pushed to the end
    //
    afterFind(id: string | number, opts: any, result: any): any {
        // From v3 docs, find
        // find(id, opts)
        // Retrieve via an adapter the record with the given primary key.
        //
        // Mapper#beforeFind will be called before calling the adapter. Mapper#afterFind will be called after calling the adapter.

        var iBaseRecordCount = 0;
        if (result instanceof ESRecord) {
            iBaseRecordCount++;
            result._rowDeleted = false;
            result._rowExists = true;
        } else {
            // Convert strings to numbers, if strings... use schema Properties
        }

        if (this.bDebugCalls) {
            console.log('afterFind: ' + this.name + ' Base Count: ' + iBaseRecordCount);
        }

        var returnValue = super.afterFind(id, opts, result);

        return returnValue;
    }

    afterFindAll(query: any, opts: any, records: any): any {
        // From v3 docs, findMany
        // findAll(query, opts)
        // Using the query argument, select records to retrieve via an adapter.
        // Mapper#beforeFindAll will be called before calling the adapter. Mapper#afterFindAll will be called after calling the adapter.

        // if is Array
        var iBaseRecordCount = 0;
        if (_.isArray(records)) {
            for (var idx in records) {
                if (records[idx] instanceof ESRecord) {
                    iBaseRecordCount++;
                    // I think the Constructor would do the same..
                    records[idx]._rowDeleted = false;
                    records[idx]._rowExists = true;
                } else {
                    // Convert strings to numbers, if strings... use schema Properties
                }
            }
        }

        if (this.bDebugCalls) {
            console.log('afterFindAll: ' + this.name + ' Base Count: ' + iBaseRecordCount);
        }

        var returnValue = super.afterFindAll(query, opts, records);

        return returnValue;
    }

    postProcessRow(aRecord: any): void {

        if (_.isEmpty(aRecord)) {
            return;
        }

        if (_.isArray(aRecord)) {
            return;
        }

        _.forEach(this._numberProperties, (aKey) => {
            if(!_.isNumber( aRecord[aKey])) //-- Avoid js-data setter overhead
               aRecord[aKey] = +aRecord[aKey];
        });

    }

    beforeFindAll(query: any, opts: any): any {
        // From v3 docs, findMany
        // findAll(query, opts)
        // Using the query argument, select records to retrieve via an adapter.
        // Mapper#beforeFindAll will be called before calling the adapter. Mapper#afterFindAll will be called after calling the adapter.

        return super.beforeFindAll(query, opts);
    }

    afterUpdate(id: string | number, props: any, opts: any, result: any): any {
        // ??? Does this even create/ return a record on Update? I don't think it does
        //
        // From v3 docs, afterUpdate
        // Using an adapter, update the record with the primary key specified by the id argument.
        // Mapper#beforeUpdate will be called before updating the record. Mapper#afterUpdate will be called after updating the record.
        //
        // From v3 docs, update
        // update(id, props, opts)
        // Using an adapter, update the record with the primary key specified by the id argument.
        // Mapper#beforeUpdate will be called before updating the record. Mapper#afterUpdate will be called after updating the record.
        //
        if (result instanceof ESRecord) {
            //result._row_deleted = false;
            result._rowExists = true;
        }

        return super.afterUpdate(id, props, opts, result);  // typings are wrong for Mapping.afterUpdate
    }

    afterUpdateMany(records: any[], opts: any, results: any): any {
        // From v3 docs, afterUpdateMany
        // afterUpdateMany(records, opts, result)
        // Mapper lifecycle hook called by Mapper#updateMany. If this method returns a promise then Mapper#updateMany will wait for the promise to resolve before continuing.

        // From v3 docs, updateMany
        // updateMany(records, opts)
        // Given an array of updates, perform each of the updates via an adapter. Each "update" is a hash of properties with which to update an record. Each update must contain the primary key of the record to be updated.
        // Mapper#beforeUpdateMany will be called before making the update. Mapper#afterUpdateMany will be called after making the update.

        for (var idx in records) {
            if (records[idx] instanceof ESRecord) {
                // technically, this should be the case...
                records[idx]._rowExists = true;
            }
        }
        return super.afterUpdateMany(records, opts, results);
    }

    afterUpdateAll(props: any, query: any, opts: any, result: any) {
        // ??? Does this even create a record ? I don't think it does
        // From v3 docs, afterUpdateAll
        // afterUpdateAll(props, query, opts, result)
        //    Mapper lifecycle hook called by Mapper#updateAll. If this method returns a promise then Mapper#updateAll will wait for the promise to resolve before continuing.

        // From v3 docs, updateAll
        // updateAll(props, query, opts)
        // Using the query argument, perform the a single updated to the selected records.
        // Mapper#beforeUpdateAll will be called before making the update. Mapper#afterUpdateAll will be called after making the update.

        return super.afterUpdateAll(props, query, opts, result);
    }

    afterDestroy(id: string | number, opts: any, result: any): any {
        // I don't think this returns a record, or handles records
        //
        // From v3 docs, afterDestroy
        // afterDestroy(id, opts, result)
        // Mapper lifecycle hook called by Mapper#destroy. If this method returns a promise then Mapper#destroy will wait for the promise to resolve before continuing.

        // From v3 docs, destroy
        // destroy(id, opts)
        // Using an adapter, destroy the record with the given primary key.
        // Mapper#beforeDestroy will be called before destroying the record. Mapper#afterDestroy will be called after destroying the record.

        return super.afterDestroy(id, opts, result);
    }

    afterDestroyAll(query: any, opts: any, result: any): any {
        // I don't think this returns a record, or handles records
        //
        // from v3 docs, afterDestroyAll
        // afterDestroyAll(data, query, opts, result)
        // Mapper lifecycle hook called by Mapper#destroyAll. If this method returns a promise then Mapper#destroyAll will wait for the promise to resolve before continuing.

        // from v3 docs, destroyAll
        // destroyAll(query, opts)
        // Destroy the records selected by query via an adapter. If no query is provided then all records will be destroyed.
        // Mapper#beforeDestroyAll will be called before destroying the records. Mapper#afterDestroyAll will be called after destroying the records.

        return super.afterDestroyAll(query, opts, result);
    }

    //</editor-fold>

    autotrimStrings(aRecord: any): void {
        const self = this;

        _.forEach(self._stringAutoTrimFields, (aProp) => {
            if (!_.isUndefined(aRecord[aProp]) && aRecord[aProp].length > self._stringPropertyLengths[aProp]) {
                aRecord[aProp] = (aRecord[aProp] as string).substr(0, self._stringPropertyLengths[aProp]);
            }
        });

    }

    autoRoundDecimals(aRecord: any): void {
        const self = this;

        _.forEach(self._numberPrecisions, (scale: number, aProp: string) => {
            if (!_.isUndefined(aRecord[aProp])) {
                const fixedStr = _.toNumber(aRecord[aProp]).toFixed(scale);
                aRecord[aProp] = parseFloat(fixedStr);
            }
        });
    }
}

export {ESRecordMapper};
